/**
 * Героини: типы ответов `/api/personas*`, `/api/persona-images/*` и вызовы записи.
 *
 * Формы списаны с `reels/web/api_personas.py` и `reels/persona.py`. Даты — ISO-строки.
 * Запуск, оценка и итерация платные: сервис просит `confirm` через 409 `CONFIRM_REQUIRED`
 * (см. `isConfirmRequired`), а повтор того же запроса гасит ключ идемпотентности `key`.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiFailure, apiFetch } from "@/lib/api";
import { post, retryQuery } from "@/lib/trends";

export type ModelInfo = {
  code: string;
  label: string;
  default: boolean;
  note: string | null;
  discovered: boolean;
  estimate?: number;
  estimate_source?: "fact" | "spike";
};

export type PersonaStatus = "draft" | "approved";

export type PersonaCard = {
  id: number;
  name: string;
  status: PersonaStatus;
  cover_path: string | null;
  images: number;
  spent: number;
  date_modify: string;
};

export type PersonaList = { personas: PersonaCard[]; models: ModelInfo[] };

export type ImageStatus = "new" | "rejected" | "approved" | "canon";

export type PersonaImage = {
  id: number;
  asset_path: string;
  kind: "portrait" | "sheet";
  status: ImageStatus;
  parent_image_id: number | null;
  depth: number;
  index: number;
  cost: number | null;
  model: string;
  date_create: string;
};

export type PromptVersion = {
  id: number;
  version: number;
  origin: "composer" | "manual" | "feedback";
  text: string;
  source_text: string | null;
  author_name: string;
  date_create: string;
};

export type Job = {
  id: number;
  model: string;
  n: number;
  status: "queued" | "running" | "done" | "failed";
  cost_estimate: number | null;
  cost_actual: number | null;
  seconds: number | null;
  error: string | null;
  http_status: number | null;
  images: PersonaImage[];
};

export type Run = {
  id: number;
  kind: "portrait" | "sheet" | "iterate" | "rebuild";
  prompt_version: number;
  parent_image_id: number | null;
  estimate: number;
  author_name: string;
  date_create: string;
  jobs: Job[];
};

export type PersonaView = {
  id: number;
  name: string;
  note: string | null;
  brief: string | null;
  status: PersonaStatus;
  author_name: string;
  face_asset_id: number | null;
  full_asset_id: number | null;
  canon: { portrait: PersonaImage | null; sheet: PersonaImage | null };
  prompts: PromptVersion[];
  runs: Run[];
  pending: number;
  spent: number;
  models: ModelInfo[];
};

export type Feedback = {
  id: number;
  good: string | null;
  bad: string | null;
  author_name: string;
  date_create: string;
  run_id: number | null;
};

export type ImageView = {
  image: PersonaImage;
  job: {
    model: string;
    n: number;
    params: Record<string, unknown>;
    prompt: string;
    cost_actual: number | null;
    seconds: number | null;
  };
  chain: PersonaImage[];
  children: PersonaImage[];
  feedbacks: Feedback[];
  last_approved_id: number | null;
  drift_warning: boolean;
};

export type Estimate = {
  total: number;
  source: "fact" | "spike" | "mixed";
  per_model: { code: string; estimate: number; source: string; jobs: number }[];
};

export type LaunchResult = { run_id: number; job_ids: number[]; estimate: number; reused: boolean };

export type IterateResult = LaunchResult & { feedback_id: number | null; prompt: PromptVersion; drift_warning: boolean };

/** Тела платных запусков: `models` — код модели → число вариантов (1–3). */
export type LaunchBody = { key: string; prompt_id: number; models: Record<string, number>; confirm?: boolean };
export type SheetBody = { key: string; models: Record<string, number>; confirm?: boolean };
export type IterateBody = {
  key: string;
  good: string;
  bad: string;
  models: Record<string, number>;
  confirm?: boolean;
};

export const personaKeys = {
  list: ["personas"] as const,
  one: (id: number) => ["persona", id] as const,
  image: (id: number) => ["persona-image", id] as const,
};

export const loadPersonas = () => apiFetch<PersonaList>("/api/personas");
export const createPersona = (name: string) => post<{ id: number }>("/api/personas", { name });
export const loadPersona = (id: number) => apiFetch<PersonaView>(`/api/personas/${id}`);
export const saveBrief = (id: number, brief: string) =>
  post<{ brief: string }>(`/api/personas/${id}/brief`, { brief });
export const composePrompt = (id: number) =>
  post<{ prompt: PromptVersion; cost: number }>(`/api/personas/${id}/compose`, {});
export const addPrompt = (id: number, text: string) =>
  post<{ prompt: PromptVersion }>(`/api/personas/${id}/prompts`, { text });
export const estimateRun = (
  id: number,
  body: { models: Record<string, number>; kind?: string; parent_image_id?: number },
) => post<Estimate>(`/api/personas/${id}/estimate`, body);
export const launchRun = (id: number, body: LaunchBody) => post<LaunchResult>(`/api/personas/${id}/launch`, body);
export const launchSheet = (id: number, body: SheetBody) => post<LaunchResult>(`/api/personas/${id}/sheet`, body);
export const launchRebuild = (id: number, body: LaunchBody) =>
  post<LaunchResult>(`/api/personas/${id}/rebuild`, body);
export const loadImage = (imageId: number) => apiFetch<ImageView>(`/api/persona-images/${imageId}`);
export const iterateImage = (imageId: number, body: IterateBody) =>
  post<IterateResult>(`/api/persona-images/${imageId}/iterate`, body);
export const setImageStatus = (imageId: number, status: "rejected" | "approved" | "canon") =>
  post<{ image: PersonaImage; persona_status: PersonaStatus }>(`/api/persona-images/${imageId}/status`, { status });

/** Героиня с автообновлением: пока в очереди есть задачи — опрос раз в 3 с. */
export function usePersona(id: number) {
  return useQuery({
    queryKey: personaKeys.one(id),
    queryFn: () => loadPersona(id),
    retry: retryQuery,
    refetchInterval: (q) => ((q.state.data?.pending ?? 0) > 0 ? 3_000 : false),
  });
}

/** Оценка выше порога: запрос надо повторить с `confirm: true` после согласия человека. */
export const isConfirmRequired = (e: unknown): boolean => e instanceof ApiFailure && e.code === "CONFIRM_REQUIRED";

export function modelLabel(models: ModelInfo[], code: string): string {
  return models.find((m) => m.code === code)?.label ?? code;
}

/** «≈ $0.013» для оценки по скачку цены, «$0.013» для факта. */
export function estimateText(value: number, source: string): string {
  const text = `$${value.toFixed(3)}`;
  return source === "spike" ? `≈ ${text}` : text;
}

/** Хвост к итогу запуска: факт — пусто, спайк — «по спайку», смесь — «частично по спайку». */
export function estimateSourceText(source: "fact" | "spike" | "mixed" | undefined): string {
  if (source === "fact") return "";
  return source === "mixed" ? " · частично по спайку" : " · по спайку";
}
